import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";

import { useGetASalesInvoice } from "../service/invoiseService";
import { useSalesInvoiceSettlement } from "../service/salesService";
import { useGetPaymentSlip } from "../service/customerService";
import Loading from "../components/common/LoaderCommon";
import { cn } from "../utils/cn";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../utils/components/ui/breadcrumb";

const PAYMENT_MODES = [
  { value: "CASH", label: "Cash" },
  { value: "CARD", label: "Card" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "ONLINE_PAYMENT", label: "Online" },
];

const inputCls =
  "h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]";
const labelCls = "text-sm font-medium text-[#52627C] mb-1.5 block";

function CustomerHistoryDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { mutate: settleInvoice } = useSalesInvoiceSettlement();
  const { data: invoiceData, isLoading } = useGetASalesInvoice(id);

  const [payment, setPayment] = useState({
    amountPaid: 0,
    paymentDate: new Date().toISOString().slice(0, 10),
    paymentMethod: "",
    chqNumber: "",
    cardNumber: "",
    onlinePaymentRefNo: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPayment((prev) => ({ ...prev, [name]: value }));
  };

  const { mutate: getPaymentSlip, isPending: isGeneratingSlip } =
    useGetPaymentSlip({
      onSuccess: (data: string) => {
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.open();
          printWindow.document.write(data);
          printWindow.document.close();
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
          }, 1000);
        } else {
          toast.error("Please allow popups to print");
        }
        navigate(-1);
      },
      onError: (error: Error) => {
        toast.error(`Failed to generate payment slip: ${error.message}`);
        navigate(-1);
      },
    });

  const handleContinue = () => {
    settleInvoice(
      {
        salesId: id,
        amountPaid: Number(payment.amountPaid),
        paymentDate: payment.paymentDate,
        paymentMode: payment.paymentMethod,
        chqNumber: payment.chqNumber,
        cardNumber: payment.cardNumber,
      },
      {
        onSuccess: (data) => {
          toast.success("Payment settled successfully");
          getPaymentSlip({ id: data });
        },
        onError: (error: Error) => {
          toast.error(`Failed to settle payment: ${error.message}`);
        },
      },
    );
  };

  if (isLoading) return <Loading />;

  const totalAmount =
    invoiceData?.invoiceProductResponses
      .map((p) => p.totalAmount)
      .reduce((a, b) => a + b, 0) ?? 0;
  const totalTax =
    invoiceData?.invoiceProductResponses
      .map((p) => p.rateOfTax)
      .reduce((a, b) => a + b, 0) ?? 0;
  const totalDiscount =
    invoiceData?.invoiceProductResponses
      .map((p) => p.discountAmount)
      .reduce((a, b) => a + b, 0) ?? 0;

  const balancePayAmount =
    (invoiceData?.balanceAmount ?? 0) - Number(payment.amountPaid);

  return (
    <div className="space-y-6 p-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/">Home</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/customer">Customer</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink
              href={`/customer-history/${invoiceData?.customer?.id}`}
            >
              Customer History
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Payment Settlement</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Products table card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E8EEF9]">
          <h2 className="text-lg font-semibold text-[#21314A]">
            Invoice Items
          </h2>
          <p className="text-sm text-[#8A9BB5] mt-0.5">
            {invoiceData?.customer?.username} —{" "}
            {invoiceData?.invoiceProductResponses?.length ?? 0} items
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F5F8FD] text-left text-xs font-semibold text-[#31425E] uppercase tracking-wide">
                <th className="px-5 py-3">Sl.</th>
                <th className="px-5 py-3">Product</th>
                <th className="px-5 py-3 text-right">Qty</th>
                <th className="px-5 py-3 text-right">Unit Price</th>
                <th className="px-5 py-3 text-right">Tax</th>
                <th className="px-5 py-3 text-right">Discount</th>
                <th className="px-5 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData?.invoiceProductResponses.map((item, idx) => (
                <tr
                  key={item.id}
                  className="border-t border-[#EEF2FA] text-sm text-[#43536A] hover:bg-[#FAFCFF] transition-colors"
                >
                  <td className="px-5 py-4 text-[#8A9BB5]">{idx + 1}</td>
                  <td className="px-5 py-4 font-medium text-[#1A2B4A]">
                    {item.product?.productName}
                  </td>
                  <td className="px-5 py-4 text-right">{item.qty}</td>
                  <td className="px-5 py-4 text-right">{Number(item.unitPrice || 0).toFixed(3)}</td>
                  <td className="px-5 py-4 text-right">{item.rateOfTax}</td>
                  <td className="px-5 py-4 text-right">
                    {Number(item.discountAmount || 0).toFixed(3)}
                  </td>
                  <td className="px-5 py-4 text-right font-semibold text-[#1A2B4A]">
                    {Number(item.totalAmount || 0).toFixed(3)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Summary row */}
        <div className="flex justify-end px-6 py-5 border-t border-[#E8EEF9]">
          <div className="w-64 space-y-0">
            {[
              {
                label: "Net Total",
                value: invoiceData?.totalAmount?.toFixed(3),
              },
              {
                label: "Amount Paid",
                value: invoiceData?.paidAmount?.toFixed(3),
              },
              { label: "Tax", value: totalTax.toFixed(3) },
              { label: "Discount", value: totalDiscount.toFixed(3) },
            ].map(({ label, value }) => (
              <div
                key={label}
                className="flex justify-between items-center py-2 border-b border-dotted border-[#EEF2FA]"
              >
                <span className="text-sm text-[#52627C]">{label}</span>
                <span className="text-sm font-medium text-[#1A2B4A]">
                  {value}
                </span>
              </div>
            ))}
            <div className="flex justify-between items-center pt-3">
              <span className="text-base font-bold text-[#1A5EF9]">Total</span>
              <span className="text-base font-bold text-[#1A5EF9]">
                {totalAmount.toFixed(3)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment settlement card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
        <h2 className="text-lg font-semibold text-[#21314A] mb-5">
          Payment Settlement
        </h2>

        {/* Payment mode pills */}
        <div className="mb-5">
          <label className={labelCls}>Payment Method</label>
          <div className="flex flex-wrap gap-2">
            {PAYMENT_MODES.map((mode) => (
              <button
                key={mode.value}
                type="button"
                onClick={() =>
                  setPayment((prev) => ({
                    ...prev,
                    paymentMethod: mode.value,
                    chqNumber: "",
                    cardNumber: "",
                    onlinePaymentRefNo: "",
                  }))
                }
                className={cn(
                  "rounded-[10px] px-5 py-2 text-sm font-semibold transition-colors",
                  payment.paymentMethod === mode.value
                    ? "bg-[#1A5EF9] text-white"
                    : "bg-[#F4F7FC] text-[#415043] hover:bg-[#E8EEF9]",
                )}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>

        {/* Payment fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div>
            <label className={labelCls}>Total Balance</label>
            <input
              type="text"
              value={
                balancePayAmount <= 0 ? "0.000" : balancePayAmount.toFixed(3)
              }
              disabled
              className={cn(inputCls, "cursor-not-allowed opacity-60")}
            />
          </div>
          <div>
            <label className={labelCls}>Amount Received</label>
            <input
              type="number"
              name="amountPaid"
              placeholder="Enter amount"
              className={inputCls}
              value={payment.amountPaid || ""}
              onChange={handleChange}
              max={balancePayAmount}
            />
          </div>
          <div>
            <label className={labelCls}>Payment Date</label>
            <input
              type="date"
              name="paymentDate"
              value={payment.paymentDate}
              onChange={handleChange}
              className={inputCls}
            />
          </div>

          {payment.paymentMethod === "CARD" && (
            <div>
              <label className={labelCls}>Card Number</label>
              <input
                type="tel"
                name="cardNumber"
                placeholder="Enter card number"
                className={inputCls}
                value={payment.cardNumber}
                onChange={handleChange}
              />
            </div>
          )}
          {payment.paymentMethod === "ONLINE_PAYMENT" && (
            <div>
              <label className={labelCls}>Online Payment Ref No</label>
              <input
                type="text"
                name="onlinePaymentRefNo"
                placeholder="Enter reference number"
                className={inputCls}
                value={payment.onlinePaymentRefNo}
                onChange={handleChange}
              />
            </div>
          )}
          {payment.paymentMethod === "CHEQUE" && (
            <div>
              <label className={labelCls}>Cheque Number</label>
              <input
                type="tel"
                name="chqNumber"
                placeholder="Enter cheque number"
                className={inputCls}
                value={payment.chqNumber}
                onChange={handleChange}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={() => navigate(-1)}
            className="h-11 rounded-[12px] border border-[#E5ECF8] px-6 text-[#52627C] font-medium text-sm hover:bg-[#F5F8FD] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            type="button"
            disabled={
              !payment.amountPaid || !payment.paymentMethod || isGeneratingSlip
            }
            className={cn(
              "h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white font-medium text-sm flex items-center gap-2 hover:bg-[#0E4FDE] transition-colors",
              (!payment.amountPaid ||
                !payment.paymentMethod ||
                isGeneratingSlip) &&
                "opacity-50 cursor-not-allowed",
            )}
          >
            {isGeneratingSlip && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirm Payment
          </button>
        </div>
      </div>
    </div>
  );
}

export default CustomerHistoryDetails;
